import PropTypes from 'prop-types';
import TopicsContainer from '@cont/Batch/TopicsContainer';
import BatchTopicGroupHeader from '@cont/Batch/BatchTopicGroupHeader';
import { useSelectorToStore } from '@hooks/reduxHooks';
import {
  fetchTopicsCovered,
  fetchTopicsWithChildren,
  fetchBatchData
} from '@actions/batches';
import SEO from '@seo/index';
import batchSubTopicsSchema from '@seo/batch/batchSubTopics';
import setupInitialProps from '@utils/setupInitialProps';
import redirect from '@cont/App/redirect';
import Layout from '@layout/index';
import escape from '@utils/escapeHTML';
import { captureException } from '@utils/sentry';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const Topics = ({ batchUID, topicUID }) => {
  const mapStateToProps = ({ batches }) => ({
    batchData: batches.data?.[batchUID]
  });

  const props = useSelectorToStore(mapStateToProps);
  const { batchData = {} } = props;
  const { thumbnail, slug, description, name, goal } = batchData;
  const metaTitle = `Topics for ${name || 'Goal'} by Unacademy`;
  const canonicalURL = `https://unacademy.com/batch/${slug}/${batchUID}/topics/${topicUID}`;
  const metaDescription = description;
  return (
    <>
      <SEO
        title={metaTitle}
        url={canonicalURL}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          images: [
            {
              url: thumbnail
            }
          ],
          canonicalURL
        }}
        schema={batchSubTopicsSchema({
          goal,
          others: [
            { name, url: `https://unacademy.com/batch/${slug}/${batchUID}` },
            { name: 'topics', url: canonicalURL }
          ]
        })}
      />

      <BatchTopicGroupHeader batchUID={batchUID} />
      <TopicsContainer topicUID={topicUID} batchUID={batchUID} />
    </>
  );
};

Topics.pageInfo = {
  name: 'Batch Topic Details',
  isMWebSupported: true
};

export default Topics;

Topics.getInitialProps = setupInitialProps(async (ctx) => {
  const { store, query, isServer } = ctx;
  const { batchUID, topicUID } = query;
  const batchData = store.getState().batches.batchDetails[batchUID];
  const promises = [];
  if (!batchData) promises.push(store.dispatch(fetchBatchData(batchUID)));
  promises.push(store.dispatch(fetchTopicsCovered(batchUID)));
  promises.push(store.dispatch(fetchTopicsWithChildren(batchUID, topicUID)));
  const [, topicsCovered, topicsWithChildren] = await Promise.all(promises);
  const invalidStatusCodes = [404, 403];
  if (
    invalidStatusCodes.includes(topicsCovered?.statusCode) ||
    invalidStatusCodes.includes(topicsWithChildren?.statusCode)
  ) {
    captureException('Error occured in topics', null, null, [
      JSON.stringify(topicsCovered)
    ]);
    captureException('Error occured in topics children', null, null, [
      JSON.stringify(topicsWithChildren)
    ]);
    return redirect(isServer, { as: '/404', href: '/404' }, ctx, {}, true);
  }
  return {
    isServer,
    batchUID: escape(batchUID),
    topicUID: escape(topicUID)
  };
}, Topics.pageInfo);

Topics.propTypes = {
  batchUID: PropTypes.string.isRequired,
  topicUID: PropTypes.string.isRequired,
  batchData: PropTypes.objectOf(PropTypes.any)
};

Topics.defaultProps = {
  batchData: {}
};
Topics.Layout = Layout;
Topics.Wrapper = PageReduxProvider;
